import React, { useState } from 'react';
import { CloseIcon } from './icons/CloseIcon';
import { UploadIcon } from './icons/UploadIcon';
import { LoadingSpinner } from './icons/LoadingSpinner';
import { AudioRecorder } from './shared/AudioRecorder';

const AudioUploader = ({ onFileUpload }) => {
    const [isDragging, setIsDragging] = useState(false);
    
    const handleFile = (file) => {
        if (file && file.type.startsWith('audio/')) {
            const reader = new FileReader();
            reader.onloadend = () => {
                onFileUpload({
                    name: file.name,
                    data: reader.result,
                    mimeType: file.type,
                });
            };
            reader.readAsDataURL(file);
        } else {
            console.error("Invalid file type. Please upload an audio file.");
        }
    };
    
    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        if (file) handleFile(file);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        const file = e.dataTransfer.files?.[0];
        if (file) handleFile(file);
    };

    const handleDragEvents = (e, isEntering) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(isEntering);
    };

    return (
         <div className="p-8">
            <input type="file" id="shot-audio-upload" accept="audio/*" onChange={handleFileChange} className="hidden" />
            <div
                onDrop={handleDrop}
                onDragOver={e => e.preventDefault()}
                onDragEnter={e => handleDragEvents(e, true)}
                onDragLeave={e => handleDragEvents(e, false)}
                className={`w-full h-48 border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer transition-colors ${isDragging ? 'border-purple-500 bg-purple-500/10' : 'border-gray-700 hover:border-gray-500'}`}
                onClick={() => document.getElementById('shot-audio-upload')?.click()}
            >
                <UploadIcon className="w-12 h-12 text-gray-500 mb-2" />
                <p className="text-gray-400">Drag & drop your audio file</p>
                <p className="text-gray-500 text-sm">or click to browse</p>
            </div>
        </div>
    );
};

export const ShotAudioModal = ({ shot, onClose, onSave }) => {
    const [mode, setMode] = useState('record');
    const [audioData, setAudioData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    
    const handleSave = () => {
        if (audioData) {
            onSave(audioData);
        }
    };
    
    return (
        <div
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={onClose} role="dialog" aria-modal="true"
        >
            <div
                className="bg-[#181820] border border-gray-700 rounded-2xl w-full max-w-lg flex flex-col overflow-hidden"
                onClick={e => e.stopPropagation()}
            >
                <header className="p-4 flex justify-between items-center border-b border-gray-700 shrink-0">
                    <h2 className="text-xl font-bold">Shot Narration</h2>
                    <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-700"><CloseIcon className="w-6 h-6" /></button>
                </header>

                <main className="flex-grow">
                    <div className="border-b border-gray-700 flex">
                        <button
                            onClick={() => setMode('record')}
                            className={`flex-1 py-3 text-center font-semibold transition-colors ${mode === 'record' ? 'bg-purple-600/20 text-purple-300 border-b-2 border-purple-400' : 'text-gray-400 hover:bg-gray-700/50'}`}
                        >
                            Record
                        </button>
                        <button
                            onClick={() => setMode('upload')}
                            className={`flex-1 py-3 text-center font-semibold transition-colors ${mode === 'upload' ? 'bg-purple-600/20 text-purple-300 border-b-2 border-purple-400' : 'text-gray-400 hover:bg-gray-700/50'}`}
                        >
                            Upload
                        </button>
                    </div>
                    
                    {audioData && (
                        <div className="p-4 bg-gray-900/50">
                            <p className="text-sm text-gray-400 mb-2">Pending audio:</p>
                            <div className="flex items-center justify-between">
                                <p className="text-green-400 truncate font-mono text-sm">{audioData.name}</p>
                                <button onClick={() => setAudioData(null)} className="text-xs text-red-400 hover:underline">Clear</button>
                            </div>
                        </div>
                    )}
                    
                    {mode === 'record' ? <AudioRecorder onRecordingComplete={setAudioData} /> : <AudioUploader onFileUpload={setAudioData} />}

                </main>
                
                <footer className="p-4 flex justify-end items-center border-t border-gray-700 shrink-0 space-x-4">
                     <button onClick={onClose} className="bg-gray-700 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded-lg transition-colors">
                        Cancel
                    </button>
                    <button 
                        onClick={handleSave}
                        disabled={!audioData || isLoading}
                        className="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 text-white font-bold py-2 px-4 rounded-lg transition-colors"
                    >
                        {isLoading ? <LoadingSpinner /> : 'Save Audio'}
                    </button>
                </footer>
            </div>
        </div>
    );
};